import axios, { AxiosError } from 'axios';
import {
  FETCH_FLEXTRACK_PAST_DUE_PERIODS_FAILURE,
  FETCH_FLEXTRACK_PAST_DUE_PERIODS_REQUEST,
  FETCH_FLEXTRACK_PAST_DUE_PERIODS_SUCCESS
} from '../constants/flexTrackToDo';
import { OdataComparator } from '@pec/aion-ui-odata/types/odataComparator';
import { OdataResponse } from '@pec/aion-ui-odata/types/odataResponse';
import { QueryBuilder } from '@pec/aion-ui-odata/builders/odataQueryBuilder';
import { RootState } from '../combineReducers';
import { sendError } from '@pec/aion-ui-core/sendError';
import { ThunkAction } from 'redux-thunk';

const { Equals, NotEquals } = OdataComparator;

// Raw report status on regionalContractorPeriods; anything other than Submitted still owes a report.
// This mirrors the Reporting/FlexTrack nav bubble (@pec/aion-ui-core/actions/fetchReportingNotificationsCount)
// so the To Do widget count stays in parity with the existing red-bubble count (CPRO-15157).
enum ReportStatus {
  Submitted = 'Submitted'
}

const { Submitted } = ReportStatus;

export type FlexTrackPastDuePeriod = {
  clientId: string;
};

const fetchFlexTrackPastDuePeriodsRequest = () =>
  ({
    type: FETCH_FLEXTRACK_PAST_DUE_PERIODS_REQUEST
  } as const);

const fetchFlexTrackPastDuePeriodsSuccess = (payload: FlexTrackPastDuePeriod[]) =>
  ({
    type: FETCH_FLEXTRACK_PAST_DUE_PERIODS_SUCCESS,
    payload
  } as const);

const fetchFlexTrackPastDuePeriodsFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_FLEXTRACK_PAST_DUE_PERIODS_FAILURE,
    error
  } as const;
};

const shouldFetchFlexTrackPastDuePeriods = ({ flexTrackToDo: { isFetching, periods } }: RootState) =>
  !isFetching && !periods;

export const fetchFlexTrackPastDuePeriods = (
  organizationId: string
): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchFlexTrackPastDuePeriodsRequest());

    const odataParams = new QueryBuilder()
      .select(['clientId'])
      .filter(f => f.filterBy('contractorId', Equals, organizationId).filterBy('reportStatus', NotEquals, Submitted))
      .toQueryParam();

    // removeUnreleasedClients keeps this in parity with the nav/landing-page FlexTrack counts
    // (@pec/aion-ui-core/actions/fetchReportingNotificationsCount), which exclude periods for clients
    // that don't yet have a released connection with the contractor.
    const params = { ...odataParams, removeUnreleasedClients: true };

    const { data } = await axios.get<OdataResponse<FlexTrackPastDuePeriod[]>>('/api/v3.01/regionalContractorPeriods', {
      params
    });

    dispatch(fetchFlexTrackPastDuePeriodsSuccess(data.value));
  } catch (error) {
    dispatch(fetchFlexTrackPastDuePeriodsFailure(error));
  }
};

export const fetchFlexTrackPastDuePeriodsIfNeeded = (
  organizationId: string
): ThunkAction<void, RootState, null, Actions> => (dispatch, getState) => {
  if (shouldFetchFlexTrackPastDuePeriods(getState())) {
    dispatch(fetchFlexTrackPastDuePeriods(organizationId));
  }
};

export type Actions =
  | ReturnType<typeof fetchFlexTrackPastDuePeriodsRequest>
  | ReturnType<typeof fetchFlexTrackPastDuePeriodsSuccess>
  | ReturnType<typeof fetchFlexTrackPastDuePeriodsFailure>;
